import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { PageHeader, Loading, ErrorState } from "@/components/common";
import { brl, pct, num } from "@/lib/format";
import { corEtapa } from "@/lib/stageColors";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from "recharts";
import { Download, FileText, TrendingUp, TrendingDown, Loader2, AlertTriangle, LineChart as ForecastIcon, ArrowRight, Info, ChevronDown } from "@/components/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import OportunidadesPanel from "@/components/OportunidadesPanel";

const PERIODS = [
  { key: "this_month", label: "Este mês" },
  { key: "last_month", label: "Mês passado" },
  { key: "quarter", label: "Trimestre" },
  { key: "year", label: "Ano" },
  { key: "ytd", label: "YTD" },
];

function Delta({ v, novo, semBase }) {
  // Item 2.8 — sem base = período anterior sem dados; não usar "Novo" nem "-100%".
  if (semBase) return <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground italic">Sem base de comparação</span>;
  if (v == null) {
    if (novo) return <span className="inline-flex items-center gap-0.5 text-xs font-medium text-ok">Novo</span>;
    return <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">—</span>;
  }
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${up ? "text-ok" : "text-bad"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{up ? "+" : "−"}{pct(Math.abs(v), 1)}
    </span>
  );
}

function Kpi({ label, value, delta, novo, sub, semBase, tip, onClick, testId }) {
  const clickable = !!onClick;
  return (
    <div
      className={`bg-card rounded-xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] ${clickable ? "cursor-pointer hover:shadow-[0_10px_30px_rgb(0,0,0,0.06)] hover:-translate-y-0.5 transition-all" : ""}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onClick(); } }}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        {tip ? <span title={tip} data-testid={`kpi-tip-${label}`} className="cursor-help text-slate-400 hover:text-slate-600"><Info className="w-3.5 h-3.5" /></span> : null}
      </div>
      <div className="text-xl sm:text-2xl font-semibold text-navy mt-2 break-words leading-tight">{value}</div>
      <div className="mt-1 flex items-center gap-2"><Delta v={delta} novo={novo} semBase={semBase} />{sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}</div>
      {clickable && <div className="text-[11px] text-brand mt-2">Ver lista →</div>}
    </div>
  );
}

// Bloco "Maiores clientes" (aba Gerencial): top consolidado da equipe + top por
// vendedor, com seletor "Ver: Todos | vendedor X". A pct_total já vem calculada
// do backend. `codparc` é usado como âncora — se o cliente tem ficha no CRM, o
// clique abre `/clientes?codparc=...`; caso contrário, só destaca a linha.
function MaioresClientes({ maiores, onOpenCliente }) {
  const [ver, setVer] = useState("__todos__");
  const porVendedor = maiores?.por_vendedor || [];
  const equipe = maiores?.equipe || [];
  const bloco = ver === "__todos__" ? null : porVendedor.find((p) => p.user_id === ver);
  const linhas = ver === "__todos__" ? equipe : (bloco?.itens || []);
  const label = ver === "__todos__" ? "Top 10 clientes · equipe" : `Top 10 clientes · ${bloco?.nome || ""}`;
  // Esconde a coluna "Última compra" quando NENHUMA linha tem a data —
  // evita a coluna cheia de "—" que não ajuda o usuário.
  const temUltima = (linhas || []).some((r) => !!r.ultima_compra_iso);
  return (
    <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-6" data-testid="maiores-clientes">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</h3>
        {porVendedor.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Ver:</span>
            <select
              value={ver}
              onChange={(e) => setVer(e.target.value)}
              data-testid="maiores-clientes-select"
              className="h-8 px-2 rounded-lg border border-input bg-white text-sm"
            >
              <option value="__todos__">Todos</option>
              {porVendedor.map((p) => (
                <option key={p.user_id} value={p.user_id}>{p.nome}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {linhas.length === 0 ? (
        <div className="text-sm text-muted-foreground">Sem dados no período.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-black/5">
                <th className="py-2 pr-3 font-semibold w-10">#</th>
                <th className="py-2 pr-3 font-semibold">Cliente</th>
                {ver === "__todos__" && <th className="py-2 pr-3 font-semibold">Vendedor</th>}
                <th className="py-2 px-2 font-semibold text-right">Faturamento</th>
                <th className="py-2 px-2 font-semibold text-right">% do total</th>
                {temUltima && <th className="py-2 pl-2 font-semibold text-right">Última compra</th>}
              </tr>
            </thead>
            <tbody>
              {linhas.map((r, i) => {
                const vends = (r.vendedores || []);
                const vendLabel = vends.length === 0 ? "—" : vends.length === 1 ? vends[0] : "vários";
                const ultima = r.ultima_compra_iso ? new Date(r.ultima_compra_iso).toLocaleDateString("pt-BR") : "—";
                return (
                  <tr key={`${r.codparc}-${i}`} className="border-b border-black/5 last:border-0 hover:bg-slate-50" data-testid={`maiores-clientes-row-${r.codparc}`}>
                    <td className="py-2.5 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2.5 pr-3">
                      <button type="button" onClick={() => onOpenCliente(r.codparc)} className="text-left text-navy hover:underline">
                        <span className="text-[11px] text-muted-foreground mr-2">{r.codparc}</span>{r.nome || "—"}
                      </button>
                    </td>
                    {ver === "__todos__" && <td className="py-2.5 pr-3 text-slate-600">{vendLabel}</td>}
                    <td className="py-2.5 px-2 text-right font-semibold text-navy">{brl(r.liquido)}</td>
                    <td className="py-2.5 px-2 text-right text-slate-600">{r.pct_total || 0}%</td>
                    {temUltima && <td className="py-2.5 pl-2 text-right text-slate-600">{ultima}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Relatorios() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("this_month");
  const [exporting, setExporting] = useState(false);
  const [panel, setPanel] = useState(null); // {mode, items?}
  const { data, loading, error, reload } = useFetch(`/relatorios?period=${period}`, [period]);
  // Resumo compacto para o link direto com "Requer atenção" e Previsão.
  const { data: atencao } = useFetch("/atencao");
  const { data: previsao } = useFetch("/previsao");

  const exportar = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/relatorios/export?format=xlsx&period=${period}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mpires-relatorio-${period}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Relatório exportado");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setExporting(false); }
  };

  // Formata variação no PDF: null → "Novo" (se novo=true) ou "—", senão "±N%".
  // jsPDF/Helvetica padrão não renderiza "Δ" nem outros unicodes fora do latin-1,
  // por isso usamos "Var. %" nas cabeceiras e o helper `pdfVar` no corpo.
  const pdfVar = (b) => {
    if (!b) return "—";
    if (b.variacao_pct == null) return b.novo ? "Novo" : "—";
    const v = b.variacao_pct;
    return `${v >= 0 ? "+" : ""}${v}%`;
  };

  const exportarPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const mX = 40; let y = 46;
      doc.setFontSize(18); doc.setTextColor(0, 62, 98);
      doc.text("Relatório Comercial · MPires", mX, y); y += 20;
      doc.setFontSize(11); doc.setTextColor(100, 116, 139);
      doc.text(`Período: ${data.period?.label || period}  ·  Gerado em ${new Date().toLocaleDateString("pt-BR")}`, mX, y); y += 14;

      autoTable(doc, {
        startY: y,
        head: [["Faturamento", "Meta", "% Atingimento", "Em andamento", "Conversão", "Oportunidades", "Ticket médio"]],
        body: [[brl(data.kpis.revenue), brl(data.kpis.meta), pct(data.kpis.atingimento, 1), brl(data.kpis.pipeline_value), data.kpis.conversion == null ? "—" : pct(data.kpis.conversion, 1), num(data.kpis.deals_count), brl(data.kpis.ticket_medio)]],
        theme: "grid", headStyles: { fillColor: [0, 138, 176] }, styles: { fontSize: 9 },
      });
      y = doc.lastAutoTable.finalY + 18;

      // Faturamento mensal (últimos 6 meses)
      const monthly = data.monthly || [];
      if (monthly.length) {
        autoTable(doc, {
          startY: y, head: [["Faturamento mensal (últimos 6 meses)", ...monthly.map((m) => m.label)]],
          body: [["Valor", ...monthly.map((m) => brl(m.valor))]],
          theme: "grid", headStyles: { fillColor: [0, 138, 176] }, styles: { fontSize: 9 },
        });
        y = doc.lastAutoTable.finalY + 18;
      }

      const cf = data.comparativos_faturamento;
      if (cf) {
        autoTable(doc, {
          startY: y, head: [["Comparativo de faturamento", "Atual", "Anterior", "Var. %"]],
          body: [
            cf.mes && ["Mês vs. mês anterior", brl(cf.mes.atual), brl(cf.mes.anterior), pdfVar(cf.mes)],
            cf.trimestre && ["Trimestre vs. anterior", brl(cf.trimestre.atual), brl(cf.trimestre.anterior), pdfVar(cf.trimestre)],
            cf.ano && ["Ano vs. ano anterior", brl(cf.ano.atual), brl(cf.ano.anterior), pdfVar(cf.ano)],
          ].filter(Boolean),
          theme: "grid", headStyles: { fillColor: [0, 62, 98] }, styles: { fontSize: 9 },
        });
        y = doc.lastAutoTable.finalY + 18;
      }

      const membros = data.comparativos_por_membro || [];
      if (membros.length) {
        const lbl = data.comparativos_membro_label || "membro";
        autoTable(doc, {
          startY: y,
          head: [[lbl.charAt(0).toUpperCase() + lbl.slice(1), "Mês atual", "Mês ant.", "Var. %", "Trim. atual", "Trim. ant.", "Var. %", "Ano atual", "Ano ant.", "Var. %"]],
          body: membros.map((m) => [
            m.nome,
            brl(m.mes.atual), brl(m.mes.anterior), pdfVar(m.mes),
            brl(m.trimestre.atual), brl(m.trimestre.anterior), pdfVar(m.trimestre),
            brl(m.ano.atual), brl(m.ano.anterior), pdfVar(m.ano),
          ]),
          theme: "striped", headStyles: { fillColor: [0, 138, 176] }, styles: { fontSize: 8 },
        });
        y = doc.lastAutoTable.finalY + 18;
      }

      // Pipeline por etapa (no /relatorios, o campo é `funnel` com `value` — no
      // gerencial é `pipeline_por_etapa` com `valor`. Usamos o que estiver
      // disponível para manter a seção sempre visível.)
      const pipe = (data.funnel || []).map((f) => ({ stage: f.stage, count: f.count, valor: f.value }));
      if (pipe.length) {
        autoTable(doc, {
          startY: y, head: [["Etapa", "Qtd.", "Valor"]],
          body: pipe.map((p) => [p.stage, p.count, brl(p.valor)]),
          theme: "grid", headStyles: { fillColor: [15, 118, 110] }, styles: { fontSize: 9 },
        });
        y = doc.lastAutoTable.finalY + 18;
      }

      const loss = data.loss_detail || [];
      if (loss.length) {
        autoTable(doc, {
          startY: y, head: [["Motivo de perda", "Qtde", "Valor"]],
          body: loss.map((l) => [l.motivo, l.count, brl(l.value)]),
          theme: "grid", headStyles: { fillColor: [220, 38, 38] }, styles: { fontSize: 9 },
        });
        y = doc.lastAutoTable.finalY + 18;
      }

      // Maiores clientes (equipe) + por vendedor.
      const mcEq = data.maiores_clientes?.equipe || [];
      if (mcEq.length) {
        autoTable(doc, {
          startY: y,
          head: [["#", "Cliente", "Vendedor", "Faturamento", "% total", "Última compra"]],
          body: mcEq.map((r, i) => [
            i + 1,
            `${r.codparc} · ${r.nome || "—"}`,
            (r.vendedores || []).length === 1 ? r.vendedores[0] : (r.vendedores?.length ? "vários" : "—"),
            brl(r.liquido),
            `${r.pct_total || 0}%`,
            r.ultima_compra_iso ? new Date(r.ultima_compra_iso).toLocaleDateString("pt-BR") : "—",
          ]),
          theme: "striped", headStyles: { fillColor: [0, 62, 98] }, styles: { fontSize: 8 },
        });
        y = doc.lastAutoTable.finalY + 18;
      }
      const mcPorV = data.maiores_clientes?.por_vendedor || [];
      for (const vv of mcPorV) {
        autoTable(doc, {
          startY: y,
          head: [[`Top 10 · ${vv.nome}`, "Faturamento", "% total", "Última compra"]],
          body: vv.itens.map((r, i) => [
            `${i + 1}. ${r.codparc} · ${r.nome || "—"}`,
            brl(r.liquido),
            `${r.pct_total || 0}%`,
            r.ultima_compra_iso ? new Date(r.ultima_compra_iso).toLocaleDateString("pt-BR") : "—",
          ]),
          theme: "striped", headStyles: { fillColor: [0, 138, 176] }, styles: { fontSize: 8 },
        });
        y = doc.lastAutoTable.finalY + 12;
      }

      // Cota Real por Gestor (mês + acumulado).
      const cog = data.cota_oficial_por_gestor || [];
      if (cog.length) {
        const MESES_LONG = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
        const sgn = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${pct(v, 1)}`;
        const p = (v) => v == null ? "—" : pct(v, 1);
        autoTable(doc, {
          startY: y,
          head: [["Cota Real por Gestor", "Período", "Cota real", "Meta distribuída", "Gordura %", "Faturado", "% cota real", "% meta dist."]],
          body: cog.flatMap((r) => {
            const mesLabel = MESES_LONG[(r.mes_sel || 1) - 1];
            return [
              [`${r.nome}\n${r.cargo}`, mesLabel, brl(r.cota_mes), brl(r.meta_mes), sgn(r.gordura_mes_pct), brl(r.faturado_mes), p(r.pct_cota_oficial_mes), p(r.pct_meta_distribuida_mes)],
              ["", `Acumulado até ${mesLabel}`, brl(r.cota_oficial_ytd), brl(r.meta_distribuida_ytd), sgn(r.gordura_pct), brl(r.faturado_ytd), p(r.pct_cota_oficial), p(r.pct_meta_distribuida)],
            ];
          }),
          theme: "striped", headStyles: { fillColor: [0, 62, 98] }, styles: { fontSize: 8 },
        });
        y = doc.lastAutoTable.finalY + 18;
      }

      // Rodapé Sankhya.
      doc.setFontSize(8); doc.setTextColor(100, 116, 139);
      doc.text(
        `Faturamento: Sankhya${data.atualizado_em_sankhya ? " · atualizado às " + new Date(data.atualizado_em_sankhya).toLocaleTimeString("pt-BR", {hour:"2-digit", minute:"2-digit"}) : ""}`,
        mX, doc.internal.pageSize.getHeight() - 20,
      );
      doc.save(`mpires-relatorio-${period}.pdf`);
      toast.success("PDF gerado");
    } catch (err) { toast.error("Falha ao gerar PDF"); }
  };

  return (
    <div>
      <PageHeader
        title="Relatórios"
        subtitle={data?.period?.label}
        action={
          <div className="hidden lg:flex items-center gap-2">
            <ExportMenu onPdf={exportarPDF} onExcel={exportar} exporting={exporting} />
          </div>
        }
      />

      {/* Em telas < 1024px, menu Exportar fica em bloco próprio */}
      <div className="lg:hidden mb-4" data-testid="export-actions-mobile">
        <ExportMenu onPdf={exportarPDF} onExcel={exportar} exporting={exporting} fullWidth />
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`period-${p.key}`}
            className={`h-9 px-4 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${period === p.key ? "bg-brand text-white" : "bg-white border border-input text-slate-600 hover:bg-slate-50"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {loading ? <Loading /> : error ? <ErrorState onRetry={reload} /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-4">
            <Kpi testId="kpi-realizado" label="Realizado (Sankhya)" value={brl(data.kpis.revenue)} delta={data.kpis.revenue_delta} semBase={data.kpis.revenue_sem_base} tip="Líquido do Sankhya (vendas − devoluções) no período." onClick={() => navigate("/faturamento")} />
            <Kpi testId="kpi-ganho-crm" label="Ganho no CRM" value={brl(data.kpis.won_value)} delta={data.kpis.won_value_delta} semBase={data.kpis.won_value_sem_base} tip="Soma do valor das oportunidades marcadas como Fechado no período." onClick={() => setPanel({ mode: "ganho" })} />
            <Kpi testId="kpi-meta" label="Meta do período" value={brl(data.kpis.meta)} delta={data.kpis.meta_delta} semBase={data.kpis.meta_sem_base} onClick={() => navigate("/faturamento")} />
            <Kpi testId="kpi-atingimento" label="% Atingimento" value={pct(data.kpis.atingimento, 1)} delta={data.kpis.atingimento_delta} semBase={data.kpis.meta_sem_base} sub={`${pct(data.kpis.atingimento_anterior, 1)} ant.`} tip="Realizado ÷ Meta do período × 100." onClick={() => navigate("/faturamento")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
            <Kpi testId="kpi-ticket" label="Ticket médio" value={data.kpis.ticket_medio == null ? "—" : brl(data.kpis.ticket_medio)} tip="Ganho no CRM ÷ nº de oportunidades ganhas no período. Sem ganhas → —." />
            <Kpi testId="kpi-andamento" label="Em andamento" value={brl(data.kpis.pipeline_value)} delta={data.kpis.pipeline_delta} semBase={data.kpis.pipeline_sem_base} onClick={() => setPanel({ mode: "andamento" })} />
            <Kpi testId="kpi-conversao" label="Conversão" value={data.kpis.conversion == null ? "—" : pct(data.kpis.conversion, 1)} delta={data.kpis.conversion_delta} tip="Ganhas ÷ (Ganhas + Perdidas) fechadas no período × 100." />
            <Kpi testId="kpi-oportunidades" label="Oportunidades" value={num(data.kpis.deals_count)} delta={data.kpis.deals_delta} semBase={data.kpis.deals_sem_base} onClick={() => setPanel({ mode: "todas" })} />
          </div>

          {/* Resumo Atenção + Previsão — atalhos diretos para as duas telas
              operacionais correspondentes (auditoria V4 item 5).
              CORRECAO_EMERGENT_RELATORIOS_GANHOS: agora abrem a lista lateral. */}
          {(atencao || previsao) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="relatorios-atencao-previsao">
              <button
                type="button"
                onClick={() => setPanel({ mode: "atencao", items: (atencao?.items || []).map((i) => ({ ...i, motivo: i.severidade === "alta" ? "crítico" : "atenção" })) })}
                data-testid="relatorios-link-atencao"
                className="text-left rounded-2xl border border-black/5 bg-white p-5 flex items-center gap-4 hover:bg-slate-50 transition"
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#F3F8FB" }}>
                  <AlertTriangle className="w-5 h-5" style={{ color: "#62717F" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Requer atenção</div>
                  <div className="text-2xl font-semibold text-navy mt-1">{num(atencao?.count || 0)}</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {(() => {
                      const c = atencao?.items?.filter((i) => i.severidade === "alta").length || 0;
                      const m = atencao?.items?.filter((i) => i.severidade === "media").length || 0;
                      return `${c} ${c === 1 ? "crítico" : "críticos"} · ${m} ${m === 1 ? "atenção" : "atenções"}`;
                    })()}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </button>

              <button
                type="button"
                onClick={() => setPanel({ mode: "previsao" })}
                data-testid="relatorios-link-previsao"
                className="text-left rounded-2xl border border-black/5 bg-white p-5 flex items-center gap-4 hover:bg-slate-50 transition"
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#F3F8FB" }}>
                  <ForecastIcon className="w-5 h-5" style={{ color: "#62717F" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Previsão ponderada · mês</div>
                  <div className="text-2xl font-semibold text-navy mt-1">{brl(previsao?.ponderado || 0)}</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {(() => { const n = previsao?.count || 0; return `${num(n)} ${n === 1 ? "oportunidade prevista" : "oportunidades previstas"} · ${previsao?.mes_label || "mês"}`; })()}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          )}

          {data.comparativos_faturamento && (
            <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Comparativos de faturamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { k: "mes", l: data.comparativos_faturamento?.mes?.dia_ref ? `Mês vs. mês anterior (até dia ${data.comparativos_faturamento.mes.dia_ref})` : "Mês vs. mês anterior" },
                  { k: "trimestre", l: "Trimestre vs. anterior" },
                  { k: "ano", l: "Ano vs. ano anterior" },
                ].map((c) => {
                  const v = data.comparativos_faturamento[c.k];
                  if (!v) return null;
                  return (
                    <div key={c.k} className="rounded-xl bg-slate-50 p-4" data-testid={`comp-fat-${c.k}`}>
                      <div className="text-xs font-medium text-muted-foreground">{c.l}</div>
                      <div className="text-xl font-semibold text-navy mt-1">{brl(v.atual)}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Delta v={v.variacao_pct} novo={v.novo} semBase={v.sem_base} />
                        <span className="text-xs text-muted-foreground">antes {brl(v.anterior)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(data.maiores_clientes?.equipe?.length > 0 || data.maiores_clientes?.por_vendedor?.length > 0) && (
            <MaioresClientes maiores={data.maiores_clientes} onOpenCliente={(codparc) => navigate(`/clientes?codparc=${codparc}`)} />
          )}

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Faturamento (6 meses)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.monthly || []} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => brl(v)} />
                  <Line type="monotone" dataKey="valor" stroke="#008AB0" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Funil do período</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.funnel || []} layout="vertical" margin={{ top: 0, right: 10, left: 30, bottom: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={80} />
                  <Tooltip formatter={(v, n) => n === "count" ? [v, "Oportunidades"] : [brl(v), "Valor"]} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {(data.funnel || []).map((f) => (
                      <Cell key={f.stage} fill={corEtapa(f.stage)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 mb-6">
            {[
              { l: "Clientes prospectados", v: data.clientes_prospectados },
              { l: "Clientes ganhos", v: data.clientes_ganhos },
              { l: "Clientes perdidos", v: data.clientes_perdidos },
            ].map((c) => (
              <div key={c.l} className="bg-card rounded-xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.l}</div>
                <div className="text-2xl font-semibold text-navy mt-2">{num(c.v)}</div>
              </div>
            ))}
          </div>

          {/* Desempenho por nível hierárquico — múltiplas tabelas, respeitando
              o escopo do usuário logado. DG/Admin recebe [Diretor, Gerente,
              Vendedor]; Diretor recebe [Gerente, Vendedor]; Gerente recebe
              [Vendedor]. Mesmas colunas e padrão de Delta em todas. */}
          {(data.comparativos_niveis || []).map((nivel) => (
            <div key={nivel.chave} className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-6 overflow-x-auto" data-testid={`comparativos-nivel-${nivel.chave}`}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                {nivel.titulo} · faturamento e comparativos
              </h3>
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-black/5">
                    <th className="py-2 pr-3 font-semibold">{nivel.label_plural}</th>
                    <th className="py-2 px-2 font-semibold text-right">Mês</th>
                    <th className="py-2 px-2 font-semibold text-right">vs. ant.</th>
                    <th className="py-2 px-2 font-semibold text-right">Trimestre</th>
                    <th className="py-2 px-2 font-semibold text-right">vs. ant.</th>
                    <th className="py-2 px-2 font-semibold text-right">Ano</th>
                    <th className="py-2 pl-2 font-semibold text-right">vs. ant.</th>
                  </tr>
                </thead>
                <tbody>
                  {nivel.rows.map((m) => (
                    <tr key={m.id} className="border-b border-black/5 last:border-0 hover:bg-slate-50" data-testid={`nivel-${nivel.chave}-row-${m.id}`}>
                      <td className="py-2.5 pr-3">
                        <div className="font-medium text-navy">{m.nome}</div>
                        {m.cargo ? <div className="text-[11px] text-muted-foreground">{m.cargo}</div> : null}
                      </td>
                      <td className="py-2.5 px-2 text-right font-semibold text-navy">{brl(m.mes.atual)}</td>
                      <td className="py-2.5 px-2 text-right"><Delta v={m.mes.variacao_pct} novo={m.mes.novo} semBase={m.mes.sem_base} /></td>
                      <td className="py-2.5 px-2 text-right text-navy">{brl(m.trimestre.atual)}</td>
                      <td className="py-2.5 px-2 text-right"><Delta v={m.trimestre.variacao_pct} novo={m.trimestre.novo} semBase={m.trimestre.sem_base} /></td>
                      <td className="py-2.5 px-2 text-right text-navy">{brl(m.ano.atual)}</td>
                      <td className="py-2.5 pl-2 text-right"><Delta v={m.ano.variacao_pct} novo={m.ano.novo} semBase={m.ano.sem_base} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* Compat: fallback quando a API não trouxe `comparativos_niveis`
              (retrocompatibilidade), mantém a tabela única antiga. */}
          {(!data.comparativos_niveis?.length) && data.comparativos_por_membro?.length > 0 && (
            <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-6 overflow-x-auto" data-testid="relatorio-por-membro">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Desempenho por {data.comparativos_membro_label || "membro"} · faturamento e comparativos
              </h3>
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-black/5">
                    <th className="py-2 pr-3 font-semibold">{(data.comparativos_membro_label || "Membro").replace(/^\w/, (c) => c.toUpperCase())}</th>
                    <th className="py-2 px-2 font-semibold text-right">Mês</th>
                    <th className="py-2 px-2 font-semibold text-right">vs. ant.</th>
                    <th className="py-2 px-2 font-semibold text-right">Trimestre</th>
                    <th className="py-2 px-2 font-semibold text-right">vs. ant.</th>
                    <th className="py-2 px-2 font-semibold text-right">Ano</th>
                    <th className="py-2 pl-2 font-semibold text-right">vs. ant.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.comparativos_por_membro.map((m) => (
                    <tr key={m.id} className="border-b border-black/5 last:border-0 hover:bg-slate-50" data-testid={`membro-row-${m.id}`}>
                      <td className="py-2.5 pr-3">
                        <div className="font-medium text-navy">{m.nome}</div>
                        {m.cargo ? <div className="text-[11px] text-muted-foreground">{m.cargo}</div> : null}
                      </td>
                      <td className="py-2.5 px-2 text-right font-semibold text-navy">{brl(m.mes.atual)}</td>
                      <td className="py-2.5 px-2 text-right"><Delta v={m.mes.variacao_pct} novo={m.mes.novo} semBase={m.mes.sem_base} /></td>
                      <td className="py-2.5 px-2 text-right text-navy">{brl(m.trimestre.atual)}</td>
                      <td className="py-2.5 px-2 text-right"><Delta v={m.trimestre.variacao_pct} novo={m.trimestre.novo} semBase={m.trimestre.sem_base} /></td>
                      <td className="py-2.5 px-2 text-right text-navy">{brl(m.ano.atual)}</td>
                      <td className="py-2.5 pl-2 text-right"><Delta v={m.ano.variacao_pct} novo={m.ano.novo} semBase={m.ano.sem_base} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.loss_detail?.length > 0 && (
            <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Motivos de perda</h3>
              <div className="space-y-2">
                {data.loss_detail.map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-navy">{l.motivo} <span className="text-muted-foreground">({l.count})</span></span>
                    <span className="font-medium text-bad">{brl(l.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rodapé Sankhya. Aparece sempre — mesmo antes do primeiro sync (mostra "—"). */}
          <p className="mt-6 text-xs text-muted-foreground text-right" data-testid="relatorios-sankhya-footer">
            Faturamento: Sankhya
            {data.atualizado_em_sankhya ? ` · atualizado às ${new Date(data.atualizado_em_sankhya).toLocaleTimeString("pt-BR", {hour:"2-digit", minute:"2-digit"})}` : " · aguardando primeira sincronização"}
          </p>
        </>
      )}
      <OportunidadesPanel
        open={!!panel}
        mode={panel?.mode}
        period={period}
        onClose={() => setPanel(null)}
        extraItems={panel?.items}
      />
    </div>
  );
}

function ExportMenu({ onPdf, onExcel, exporting, fullWidth }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div className={`relative ${fullWidth ? "w-full" : ""}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={exporting}
        data-testid="export-menu-btn"
        className={`${fullWidth ? "w-full justify-center" : ""} inline-flex items-center gap-2 h-[38px] px-4 rounded-lg bg-white border border-[#D5DEE5] text-navy text-sm font-semibold hover:bg-slate-50 disabled:opacity-60`}
      >
        {exporting ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#62717F" }} /> : <Download className="w-4 h-4" style={{ color: "#62717F" }} />}
        Exportar
        <ChevronDown className="w-3.5 h-3.5" style={{ color: "#62717F" }} />
      </button>
      {open ? (
        <div className="absolute right-0 mt-1 z-30 bg-white border border-[#D5DEE5] rounded-lg shadow-lg py-1 min-w-[180px]" data-testid="export-menu">
          <button
            onClick={() => { setOpen(false); onPdf(); }}
            data-testid="export-pdf-btn"
            className="w-full text-left px-3 py-2 text-sm text-navy hover:bg-slate-50 flex items-center gap-2"
          >
            <FileText className="w-4 h-4" style={{ color: "#62717F" }} /> PDF
          </button>
          <button
            onClick={() => { setOpen(false); onExcel(); }}
            data-testid="export-btn"
            className="w-full text-left px-3 py-2 text-sm text-navy hover:bg-slate-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4" style={{ color: "#62717F" }} /> Excel
          </button>
        </div>
      ) : null}
    </div>
  );
}
